'use client'

import { AnimatePresence, LazyMotion, domAnimation } from 'framer-motion'
import Image from 'next/image'
import { useState } from 'react'

import type { PortfolioProps } from '@/types/portfolio.types'
import { Dialog } from '@/ui/components/portfolio/view/dialog'

/**
 * La planche de chantier.
 *
 * Cinq tirages posés en composition asymétrique, chacun légendé en
 * monospace. La légende reprend le texte alternatif existant : l'image
 * porte donc alt="" et son nom accessible vient du <figcaption>, pour
 * éviter la double lecture par les lecteurs d'écran.
 */

// Placement desktop de chaque tirage sur la grille 12 colonnes.
const PLACEMENT = [
  'desktop:col-span-7 desktop:col-start-1 aspect-4/3',
  'desktop:col-span-4 desktop:col-start-9 aspect-3/4 desktop:translate-y-14',
  'desktop:col-span-3 desktop:col-start-2 aspect-square',
  'desktop:col-span-4 desktop:col-start-5 aspect-4/3 desktop:-translate-y-6',
  'desktop:col-span-3 desktop:col-start-10 aspect-square desktop:translate-y-8',
]

export default function PortfolioGrid({ images }: PortfolioProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const board = images.slice(0, 5)

  return (
    <LazyMotion features={domAnimation}>
      <div className="w-full">
        {/* Mobile : défilement horizontal calé */}
        <div className="flex tablet:hidden snap-x snap-mandatory gap-4 overflow-x-auto pb-4 -mx-5 px-5">
          {board.map((image, index) => (
            <figure key={`m-${image.src}`} className="w-[82%] flex-none snap-center">
              <button
                type="button"
                onClick={() => setOpenIndex(index)}
                aria-label={`Agrandir : ${image.alt}`}
                className="relative block aspect-4/5 w-full overflow-hidden rounded-[3px] bg-trace"
              >
                <Image
                  src={image.src}
                  alt=""
                  fill
                  sizes="82vw"
                  quality={80}
                  className="object-cover"
                  priority={index === 0}
                />
              </button>
              <figcaption className="mt-3 font-mono text-[0.6875rem] leading-snug text-graphite">
                {image.alt}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Desktop : planche asymétrique */}
        <div className="hidden tablet:grid grid-cols-2 desktop:grid-cols-12 gap-6 desktop:gap-x-6 desktop:gap-y-12">
          {board.map((image, index) => (
            <figure
              key={image.src}
              className={`group ${PLACEMENT[index] ?? 'desktop:col-span-4'}`}
            >
              <button
                type="button"
                onClick={() => setOpenIndex(index)}
                aria-label={`Agrandir : ${image.alt}`}
                className="relative block h-full w-full overflow-hidden rounded-[3px] bg-trace"
              >
                <Image
                  src={image.src}
                  alt=""
                  fill
                  sizes="(max-width: 1023px) 50vw, 40vw"
                  quality={85}
                  priority={index === 0}
                  className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
              </button>
              <figcaption className="mt-3 max-w-[36ch] font-mono text-[0.6875rem] leading-snug text-graphite">
                {image.alt}
              </figcaption>
            </figure>
          ))}
        </div>

        {images.length > 5 && (
          <div className="mt-12 desktop:mt-20 border-t border-ink/15 pt-6">
            <button
              type="button"
              onClick={() => setOpenIndex(0)}
              className="link-draw font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-ink"
            >
              Afficher toutes les photos
            </button>
          </div>
        )}

        <AnimatePresence>
          {openIndex !== null && (
            <Dialog
              index={openIndex}
              images={images}
              onClose={() => setOpenIndex(null)}
            />
          )}
        </AnimatePresence>
      </div>
    </LazyMotion>
  )
}
